import { useMutation } from '@apollo/client';
import { UpdateOrganisationTabPreferencesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateOrganisationTabPreferencesTRPCInput } from './useUpdateOrganisationTabPreferencesTRPC';
import { useUpdateOrganisationTabPreferencesTRPC } from './useUpdateOrganisationTabPreferencesTRPC';

type MutateArgs = { variables: UpdateOrganisationTabPreferencesTRPCInput };

/**
 * Flag-gated wrapper for the `updateOrganisationTabPreferences`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 *
 * Returns an Apollo-compatible tuple `[mutate]` so consumers can keep
 * the `[mutate] = useMutation(...)` / `await mutate({ variables })`
 * shape.
 */
export const useUpdateOrganisationTabPreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateOrganisationTabPreferencesGraphQL] = useMutation(
    UpdateOrganisationTabPreferencesDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_tab_preference');
        evictField(cache, 'user_tab_preference');
      },
    }
  );

  const trpcMutation = useUpdateOrganisationTabPreferencesTRPC();

  const mutate = async ({ variables }: MutateArgs) => {
    if (trpcEnabled) {
      return trpcMutation.updateOrganisationTabPreferences(variables);
    }

    const result = await updateOrganisationTabPreferencesGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update organisation tab preferences');
    }

    return result.data;
  };

  return [mutate] as const;
};
